import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useJobFilterStore } from "../../store/useJobFilterStore";
import { useCandidateProfileStore } from "../../store/useCandidateProfileStore";
import { useAvailabilityStore } from "../../store/useAvailabilityStore";
import UserPreference from "./UserPreference";
import EditProfileModal from "../../components/modals/EditProfileModal";

const REQUIRED_FIELD_LABELS: Record<string, { id: string; en: string }> = {
  photo: { id: "Foto", en: "Photo" },
  name: { id: "Nama Lengkap", en: "Full Name" },
  phone: { id: "Nomor Telepon", en: "Phone Number" },
  birth_date: { id: "Tanggal Lahir", en: "Birth Date" },
  gender_id: { id: "Jenis Kelamin", en: "Gender" },
  country_id: { id: "Negara", en: "Country" },
  state_id: { id: "Provinsi", en: "State" },
  city_id: { id: "Kota", en: "City" },
  join_availability_id: { id: "Ketersediaan Gabung", en: "Join Availability" },
  experience_year_id: { id: "Tahun Pengalaman", en: "Experience Year" },
  experience_level_id: { id: "Level Pengalaman", en: "Experience Level" },
  degree_id: { id: "Gelar", en: "Degree" },
  current_salary: { id: "Gaji Saat Ini", en: "Current Salary" },
  expected_salary: { id: "Ekspektasi Gaji", en: "Expected Salary" },
  resume: { id: "Resume", en: "Resume" },
};

const REQUIRED_ARRAY_FIELD_LABELS: Record<string, { id: string; en: string }> = {
  skills: { id: "Skill", en: "Skills" },
  languages: { id: "Bahasa", en: "Languages" },
  job_preferences: { id: "Preferensi Pekerjaan", en: "Job Preferences" },
  educations: { id: "Riwayat Pendidikan", en: "Education History" },
};


export default function SidebarRight() {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState("");

  const [selectedJoinNotice, setSelectedJoinNotice] = useState<number>();
  const [availableForHiring, setAvailableForHiring] = useState<boolean | null>(null);


  const { profile, fetchCandidateProfile } = useCandidateProfileStore();

  const { jobPreferences, joinNotice, fetchFilters } = useJobFilterStore();

  useEffect(() => {
    fetchCandidateProfile();
  }, [fetchCandidateProfile]);

  const openModal = (type: string) => {
    setModalContent(type);
    setModalOpen(true);
  };


  const [preference, setPreference] = useState<any[]>([]);

  useEffect(() => {
    if (!profile) return;

    setPreference(
      profile.job_preferences?.map((s: any) => ({
        preferenceName: s.id,
      })) || []
    );

    setSelectedJoinNotice(profile.join_availability_id);

    setAvailableForHiring(profile.available_for_hiring);
  }, [profile]);

  const location = useLocation();
  const lang = location.pathname.startsWith("/id") ? "id" : "en";

  useEffect(() => {
    if (lang) fetchFilters(lang);
  }, [lang, fetchFilters]);

  const {
    updateJoinAvailability,
    updateAvailabilityStatus,
    loading,
  } = useAvailabilityStore();

  const handleStatusChange = async (value: boolean) => {
    setAvailableForHiring(value);
    await updateAvailabilityStatus({
      availableForHiring: value,
    });
    fetchCandidateProfile();

  };

  const handleJoinAvailabilityChange = async (id: number) => {
    setSelectedJoinNotice(id);

    await updateJoinAvailability({
      joinAvailabilityId: id,
    });
    fetchCandidateProfile();
  };
  const calculateProfileCompletion = (profile: any, lang: "id" | "en") => {
    let total = 0;
    let filled = 0;
    const missing: string[] = [];

    Object.entries(REQUIRED_FIELD_LABELS).forEach(([key, label]) => {
      total++;
      if (profile[key] !== null && profile[key] !== "") {
        filled++;
      } else {
        missing.push(label[lang]);
      }
    });

    Object.entries(REQUIRED_ARRAY_FIELD_LABELS).forEach(([key, label]) => {
      total++;
      if (Array.isArray(profile[key]) && profile[key].length > 0) {
        filled++;
      } else {
        missing.push(label[lang]);
      }
    });

    const percent = Math.round((filled / total) * 100);

    return {
      percent,
      completed: percent === 100,
      missing,
    };
  };


  const completion = profile
    ? calculateProfileCompletion(profile, lang)
    : null;



  return (
    <div className="space-y-6">

      {/* PROFILE COMPLETENESS */}
      <div className="bg-white p-4 rounded-xl border space-y-4">
        <h3 className="text-lg font-semibold">
          {lang === "id" ? "Kelengkapan Profil Untuk Melamar Pekerjaan" : "Profile Completeness For Apply Job"}
        </h3>

        {completion && (
          <>
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className={`h-2 transition-all duration-300 ${completion.completed ? "bg-green-500" : "bg-primary"
                  }`}
                style={{ width: `${completion.percent}%` }}
              />
            </div>

            {/* Status */}
            <div className="flex justify-between items-center text-sm">
              <span>
                {completion.completed
                  ? lang === "id"
                    ? "Profil sudah lengkap"
                    : "Profile completed"
                  : lang === "id"
                    ? "Profil belum lengkap"
                    : "Profile incomplete"}
              </span>
              <span className="font-semibold">{completion.percent}%</span>
            </div>

            {/* Missing Fields */}
            {!completion.completed && completion.missing.length > 0 && (
              <div className="text-sm space-y-2">
                <p className="font-medium text-gray-700">
                  {lang === "id"
                    ? "Data yang belum diisi:"
                    : "Missing information:"}
                </p>

                <ul className="list-disc list-inside text-gray-500 space-y-1">
                  {completion.missing.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>



      {/* STATUS AVAILABILITY */}
      <div className="bg-white p-4 rounded-xl border space-y-4">
        <h3 className="text-lg font-semibold">
          {lang === "id" ? "Ketersediaan Status" : "Status Availability"}
        </h3>
        <div className="space-y-2">
          <label className="flex gap-2 items-center">
            <input
              type="radio"
              name="statusAvailability"
              checked={availableForHiring === true}
              onChange={() => handleStatusChange(true)}
            />
            Active looking for Job
          </label>

          <label className="flex gap-2 items-center">
            <input
              type="radio"
              name="statusAvailability"
              checked={availableForHiring === false}
              onChange={() => handleStatusChange(false)}
            />
            Not available
          </label>

        </div>
      </div>

      {/* JOIN AVAILABILITY (FROM API) */}
      <div className="bg-white p-4 rounded-xl border space-y-4">
        <h3 className="text-lg font-semibold">
          {lang === "id" ? "Ketersediaan Gabung" : "Join Availability"}<span className="text-red-500">*</span>
        </h3>

        <div className="space-y-2">
          {joinNotice?.map((item) => (
            <label key={item.id} className="flex gap-2 items-center">
              <input
                type="radio"
                name="joinAvailability"
                value={item.id}
                checked={selectedJoinNotice === item.id}
                onChange={() => handleJoinAvailabilityChange(item.id)}
              />
              {item.name}
            </label>
          ))}
        </div>
      </div>

      {/* JOB PREFERENCES */}
      <UserPreference
        openModal={openModal}
        preferences={preference ?? []}
        jobPreferences={jobPreferences}
        lang={lang}
      />

      <EditProfileModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        type={modalContent}
        preference={preference}
        setPreference={setPreference}
      />
    </div>
  );
}
